/**
 * Accordion - two designs under one name, told apart by `layout`.
 *
 * **`list`** - Figma `6377:89221` and `6377:88809`, on Cytric and MY-WCP: rows one under
 * another, each opening and closing on its own. `<details>`, so the browser does the work.
 *
 * **`columns`** - Figma `6502:22918`, on Zespol. Two columns of panels, each column an
 * accordion of its own with exactly one panel open: a blue card with the title, a close
 * glyph and the body, over or under grey bars holding only a title and a plus. Opening a
 * bar closes the open card beside it, so neither a panel nor the block ever changes
 * height - the file draws all four states of the two-by-two block, each 1360x435.
 *
 * **One height for every open panel.** The file's cards are 307 tall because its longest
 * text fits in 307; an editor's may not, and a card that grew for one entry would move the
 * block when another opened. So the texts are measured and every open card is sized for
 * the longest - the one piece of layout maths here, and the reason there is script at all.
 * The measured value is a CSS variable, so the open and close are still CSS transitions:
 * the arriving body grows exactly as fast as the leaving one shrinks, and the column keeps
 * its height through the animation as well as either side of it.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { AccordionList, Blocks, Heading, SectionShell } from '../primitives';
import type { AccordionItem, AccordionSection } from '../../types/sections';
import { Icon } from '../../components/ui/Icon';

export default function Accordion({ data }: { data: AccordionSection }) {
  const items = data.items ?? [];

  return (
    <SectionShell settings={data.settings} name="accordion">
      {data.heading ? <Heading>{data.heading}</Heading> : null}
      {data.layout === 'columns'
        // A panel's title is a heading one level under the section's, or under the page's
        // when the section has none - Zespol's block sits straight under the page's h1.
        ? <AccordionColumns items={items} titleLevel={data.heading ? 'h3' : 'h2'} />
        : <AccordionList items={items} />}
    </SectionShell>
  );
}

/**
 * An open card measured on 6377:87978: 307 tall, of which the title row takes 116 - 36
 * above a 48px glyph, 32 under it. The text sits at the foot of the other 191, 36 above the
 * card's bottom edge. A longer text makes every open card taller, never shorter.
 */
const BODY_MIN = 191;
const BODY_BOTTOM = 36;

const TRANSITION = 'duration-300 motion-reduce:transition-none';

function AccordionColumns({ items, titleLevel: Title }: { items: AccordionItem[]; titleLevel: 'h2' | 'h3' }) {
  const root = useRef<HTMLDivElement>(null);
  const id = useId();
  // Which panel is open in each column, by its place in the column. Each column starts
  // with its first panel open, as the file's first state does (6377:87976).
  const [open, setOpen] = useState<number[]>([]);

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const texts = Array.from(el.querySelectorAll<HTMLElement>('[data-accordion-text]'));
    // A closed body is clipped to nothing, not removed, so its text keeps its natural
    // height and can be measured with the rest; the observer re-measures when a width
    // change rewraps it.
    const measure = () => {
      const tallest = Math.max(0, ...texts.map((text) => text.offsetHeight));
      el.style.setProperty('--accordion-body', `${Math.max(BODY_MIN, tallest + BODY_BOTTOM)}px`);
    };
    measure();
    const observer = new ResizeObserver(measure);
    texts.forEach((text) => observer.observe(text));
    return () => observer.disconnect();
  }, [items]);

  if (!items.length) return null;

  // The first half of the list is the left column and the rest the right, so the order an
  // editor sees in the CMS is the order a screen reader - and a phone, where the columns
  // stack - gets them in.
  const half = Math.ceil(items.length / 2);
  const columns = [items.slice(0, half), items.slice(half)].filter((column) => column.length);

  return (
    <div ref={root} className="grid items-start gap-4 lg:grid-cols-2">
      {columns.map((column, c) => {
        const current = Math.min(open[c] ?? 0, column.length - 1);
        return (
          <div key={c} className="flex flex-col gap-4">
            {column.map((item, position) => {
              const isOpen = position === current;
              const key = `${id}-${c}-${position}`;
              return (
                <div
                  key={position}
                  className={`rounded-l transition-colors ${TRANSITION} ${
                    isOpen ? 'bg-blue-500 text-white' : 'bg-grey-200 text-ink-strong'
                  }`}
                >
                  <Title className="text-h4">
                    {/* The open panel's button stays in the tab order but does nothing: one
                        panel per column is always open, which is what keeps the height. */}
                    <button
                      id={`${key}-title`}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={`${key}-body`}
                      aria-disabled={isOpen || undefined}
                      onClick={() => {
                        if (isOpen) return;
                        setOpen((previous) => {
                          const next = [...previous];
                          next[c] = position;
                          return next;
                        });
                      }}
                      className={`flex w-full items-center justify-between gap-6 rounded-l px-9 pb-8 text-left transition-[padding] focus-visible:-outline-offset-4 ${TRANSITION} ${
                        isOpen ? 'cursor-default pt-9 focus-visible:outline-white' : 'pt-8'
                      }`}
                    >
                      <span>{item.title}</span>
                      <Icon name={isOpen ? 'close-circle' : 'plus-circle'} size={48} />
                    </button>
                  </Title>

                  {/* Rows from 0fr to 1fr, so the height animates without being measured.
                      A closed body goes `invisible` once it has finished closing: nothing in
                      it can take focus or be read out while it cannot be seen. */}
                  <div
                    id={`${key}-body`}
                    role="region"
                    aria-labelledby={`${key}-title`}
                    className={`grid transition-[grid-template-rows,visibility] ${TRANSITION} ${
                      isOpen ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div
                        className="flex flex-col justify-end px-9 pb-9"
                        style={{ minHeight: `var(--accordion-body, ${BODY_MIN}px)` }}
                      >
                        <div data-accordion-text>
                          <Blocks
                            value={item.body}
                            className="max-w-none text-body-md text-inherit [&>p:last-child]:mb-0 [&_a:hover]:text-inherit [&_a]:text-inherit"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
